import { I18n } from "@assets/strings";
import { useObservable } from "micro-observables";
import React, { JSX } from "react";
import styled from "styled-components";
import { allAfricanCountrycodes } from "@domain/application/africanCountryCode";
import { isPhoneNumber } from "@utils/regex";
import { PrimaryBold } from "@ui/components/containers/primary";
import { InfoBulle } from "@ui/components/infoBulle";
import { CountryDatalist } from "@ui/components/inputs/countryDatalist";
import { DatePicker } from "@ui/components/inputs/datePicker";
import { NationalityDatalist } from "@ui/components/inputs/nationalityDatalist";
import { SimpleTextInput } from "@ui/components/inputs/simpleTextInput";
import { stack, Stack } from "@ui/components/layout";
import { mobile } from "@ui/styles/utils";
import { useApplicationEditorController } from "@ui/pages/application/applicationEditorPage/applicationEditorController/applicationEditorControllerContext";

export const PersonalInformationEditor: React.FC = ({ ...htmlProps }) => {
	const controller = useApplicationEditorController();

	const { firstName, familyName, dateBirth, countryResidence, nationality, email, phoneNumber } = useObservable(
		controller.application
	);

	const getFormItem = (
		field: "firstname" | "familyname" | "date_birth" | "country_residence" | "nationality"
	): JSX.Element => (
		<PrimaryBold>
			{I18n.t(`application.form.${field}.label`)} <span style={{ color: "red" }}>*</span>
		</PrimaryBold>
	);

	return (
		<Container {...htmlProps}>
			<ContentBlocInput>
				<Stack gap={20} align="stretch">
					<InfoEdition>
						{getFormItem("firstname")}
						<SimpleTextInput
							value={firstName}
							onChange={(v) => controller.setFirstName(v)}
							placeholder={I18n.t("application.form.firstname.placeholder")}
							isApplication
						/>
					</InfoEdition>
					<InfoEdition>
						{getFormItem("familyname")}
						<SimpleTextInput
							value={familyName}
							onChange={(v) => controller.setLastName(v)}
							placeholder={I18n.t("application.form.familyname.placeholder")}
							isApplication
						/>
					</InfoEdition>
					<DatepickerEdition>
						{getFormItem("date_birth")}
						<DatePicker
							value={dateBirth}
							onChange={(date) => controller.setDateOfBirth(date)}
							minYear={1922}
							hideDay={false}
							customStyle={true}
						/>
					</DatepickerEdition>
					<InfoEdition>
						<StyleCustomInput>
							<PrimaryBold>
								{I18n.t("application.form.country_residence.label")} <span style={{ color: "red" }}>*</span>
							</PrimaryBold>
							<InfoBulle text={I18n.t("application.form.country_residence.tooltip")} />
						</StyleCustomInput>
						<CountryDatalist
							id="countries"
							value={countryResidence}
							onChange={(v) => controller.setCountry(v)}
							placeholder={I18n.t("application.form.country_residence.placeholder")}
							options={allAfricanCountrycodes}
						/>
					</InfoEdition>
					<InfoEdition>
						<StyleCustomInput>
							<PrimaryBold>
								{I18n.t("application.form.nationality.label")} <span style={{ color: "red" }}>*</span>
							</PrimaryBold>
							<InfoBulle text={I18n.t("application.form.nationality.tooltip")} />
						</StyleCustomInput>
						<NationalityDatalist
							id="nationalities"
							value={nationality}
							onChange={(v) => controller.setNationality(v)}
							placeholder={I18n.t("application.form.nationality.placeholder")}
						/>
					</InfoEdition>
					<InfoEdition>
						<StyleCustomInput>
							<PrimaryBold>{I18n.t("application.form.phone_number.label")}</PrimaryBold>
						</StyleCustomInput>
						<SimpleTextInput
							value={phoneNumber ?? ""}
							onChange={(v) => {
								if (isPhoneNumber(v) || v === "" || v === "+") {
									controller.setPhoneNumber(v);
								}
							}}
							placeholder={I18n.t("application.form.phone_number.placeholder").replaceAll("'", "")}
							isApplication
						/>
					</InfoEdition>
					<InfoEdition>
						<StyleCustomInput>
							<PrimaryBold>{I18n.t("application.form.email.label")}</PrimaryBold>
							<InfoBulle text={I18n.t("application.form.email.tooltip")} />
						</StyleCustomInput>
						<SimpleTextInput
							value={email}
							onChange={(v) => controller.setEmail(v)}
							placeholder={I18n.t("application.form.email.placeholder")}
							isApplication
							disabled
						/>
					</InfoEdition>
				</Stack>
			</ContentBlocInput>
		</Container>
	);
};

const Container = styled.div`
	display: flex;
	flex-direction: column;
	margin-top: 60px;
	gap: 20px;

	@media ${mobile} {
		margin-top: 80px;
	}
`;

const InfoEdition = styled.div`
	${stack(10, "flex-start", "stretch")};
`;

const DatepickerEdition = styled.div`
	${stack(10, "flex-start", "stretch")};
	width: 50%;
`;

const ContentBlocInput = styled.div`
	width: 70%;
	@media ${mobile} {
		width: 100%;
	}
`;

const StyleCustomInput = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
`;

// const Info = styled.div`
// 	${row(16, "flex-start", "baseline")};
// 	${({ theme }) => theme.textStyles.description};
// 	font-size: 16px !important;
// 	line-height: 25px;
// 	width: 87%;
// `;
